import React, { useLayoutEffect } from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { IconSpinner } from '../../components';
import { ensureCurrentUser } from '../../util/data';
import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { getUserTypeDefaultRedirectPath } from '../../util/userTypeRedirects';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';
import {
  isOAuthOrOpenIdCallbackUrl,
  MARKETING_SITE_ORIGIN,
  shouldRedirectRootToMarketingSite,
} from '../../util/marketingSiteRedirect';
import * as log from '../../util/log';

import css from './LandingPage.module.css';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const LandingPageComponent = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    authInfoLoaded,
    isAuthenticated,
    currentUser,
  } = props;
  const location = useLocation();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const redirectToMarketingSite = shouldRedirectRootToMarketingSite(
    location.search,
    location.hash || ''
  );
  const isRootPath = location.pathname === '/';

  const user = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!user.id;
  const isBanned = user?.attributes?.banned === true;

  const shouldDeferRedirect =
    isRootPath &&
    redirectToMarketingSite &&
    (!authInfoLoaded || (isAuthenticated && !currentUserLoaded));

  const shouldRedirectAuthenticatedUser =
    isRootPath &&
    redirectToMarketingSite &&
    authInfoLoaded &&
    isAuthenticated &&
    currentUserLoaded &&
    !isBanned;

  const shouldRedirectToMarketing =
    isRootPath &&
    redirectToMarketingSite &&
    authInfoLoaded &&
    (!isAuthenticated || (isAuthenticated && currentUserLoaded && isBanned));

  useLayoutEffect(() => {
    if (!shouldRedirectAuthenticatedUser || typeof window === 'undefined') {
      return;
    }
    const userType = user?.attributes?.profile?.publicData?.userType;
    const path = getUserTypeDefaultRedirectPath(userType, currentUser, routeConfiguration);
    history.replace(path);
  }, [
    shouldRedirectAuthenticatedUser,
    user,
    currentUser,
    routeConfiguration,
    history,
  ]);

  useLayoutEffect(() => {
    if (!shouldRedirectToMarketing || typeof window === 'undefined') {
      return;
    }
    const search = location.search || '';
    const hash = location.hash || '';
    log.navigationDiagnostic('landing_marketing_redirect', {
      reason: 'marketing_redirect',
      searchLength: search.length,
      hashLength: hash.length,
      oauthCallbackDetected: isOAuthOrOpenIdCallbackUrl(search, hash),
    });
    window.location.href = MARKETING_SITE_ORIGIN;
  }, [shouldRedirectToMarketing, location.search, location.hash]);

  if (shouldDeferRedirect || shouldRedirectToMarketing || shouldRedirectAuthenticatedUser) {
    return (
      <div
        className={css.redirectPlaceholder}
        data-testid="landing-redirect-placeholder"
        aria-busy="true"
        aria-live="polite"
      >
        <IconSpinner />
      </div>
    );
  }

  return (
    <PageBuilder
      pageAssetsData={pageAssetsData?.[camelize(ASSET_NAME)]?.data}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
  authInfoLoaded: bool,
  isAuthenticated: bool,
  currentUser: propTypes.currentUser,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const { authInfoLoaded, isAuthenticated } = state.auth || {};
  const { currentUser } = state.user || {};
  return { pageAssetsData, inProgress, error, authInfoLoaded, isAuthenticated, currentUser };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;
